import {
  DashboardState,
  EProjectType,
  ESdChangeReason,
  IDisplayItem,
  IDisplayItemComponent,
  PlaceToType
} from "@kuika/kuika-cl-model";
import classNames from "classnames";
import React from "react";
import { IScreenDesignContextState } from "../../../../contexts/screen-design-context/screen-design-context";
import { IApplyPropertyGridChangesToDisplayParameters } from "../../../../models/interface/function-parameters";
import { IWithSDProps, withSD } from "../../../../shared/hoc/screen-design-consumer";
import { PureSdComponent } from "../../../../shared/pure-sd";
import { isChildComponentSupportedByProjectTarget } from "../../../../shared/utility/child-component-target-support";
import { KMainFunctions } from "../../../../shared/utility/main-functions";
import { ScreenDesignHelper } from "../../../../shared/utility/screen-design-helper";
import { StaticTableHelper } from "../../../../shared/utility/static-table-helper";
import KuikaButton from "../../../ui-kit/kuika-button/kuika-button";
import KuikaComponentIcon from "../../../ui-kit/kuika-component-icon/kuika-component-icon";
import KuikaDropdownMenu, { KuikaDropdownMenuItemType } from "../../../ui-kit/kuika-dropdown-menu/kuika-dropdown-menu";
import KuikaOverflowText from "../../../ui-kit/kuika-overflow-text/kuika-overflow-text";
import { OVERFLOW_REVEAL_HOST_CLASS } from "../../../ui-kit/kuika-overflow-text/overflow-reveal-controller";
import KuikaSelectbox from "../../../ui-kit/kuika-selectbox/kuika-selectbox";
import KuikaSelectionIndicator from "../../../ui-kit/kuika-selection-indicator/kuika-selection-indicator";
import KuikaSvg from "../../../ui-kit/kuika-svg/kuika-svg";
import KuikaTooltip from "../../../ui-kit/kuika-tooltip/kuika-tooltip";
import KuikaSmallMediumText from "../../../ui-kit/typography/SmallMediumText/SmallMediumText";
import KuikaText from "../../../ui-kit/typography/Text/Text";
import "./component-children-settings.scss";
import { buildChildrenTree, IChildrenTreeDeps, IChildrenTreeNode } from "./component-children-tree-model";

export interface IComponentChildrenSettingsState {
  collapsedNodes: number[];
}

export interface IComponentChildrenSettingsProps {
  menuSelectValue: string;
  setMenuSelectValue: (value: string) => void;
  disableChildActions: boolean;
}

/**
 * A table has exactly one header and one row. `getSelectedNodeStructure` already drops them, but it
 * looks at the SELECTED item's children, so a table drawn while one of its own descendants is
 * selected still offered a second header and a second row.
 */
const SINGLETON_CHILDREN: string[] = [
  "TableHeader",
  "ReportTableHeader",
  "StaticTableHeader",
  "TableRow",
  "ReportTableRow",
  "StaticTableRow"
];

/** One 12px rail per ancestor level; the row's own padding sits in front of the first one. */
const RAIL_WIDTH_PX: number = 12;

class ComponentChildrenSettings extends PureSdComponent<
  IComponentChildrenSettingsProps & IWithSDProps,
  IComponentChildrenSettingsState
> {
  constructor(props: IComponentChildrenSettingsProps & IWithSDProps) {
    super(props);
    this.state = {
      collapsedNodes: []
    };
  }

  componentDidUpdate(prevProps: IComponentChildrenSettingsProps & IWithSDProps) {
    const sd = this.props.sd.getScreenDesign();
    const selectedDi = this.props.sd.getSelectedDisplayItem(sd);
    const oldKuikaId: string = prevProps.sd.selectedKuikaIds[0];
    const newKuikaId: string = this.props.sd.selectedKuikaIds[0];
    const isMobileApp = this.props.sd.project?.projectType === EProjectType.Mobile;
    if (oldKuikaId !== newKuikaId) {
      if (selectedDi?.item.name === "Menu" && !isMobileApp) {
        this.props.setMenuSelectValue(
          selectedDi.item.properties.find((x) => x.name == "isDynamic")?.symbol?.fixedValue === "true"
            ? "dynamic"
            : "static"
        );
      } else {
        this.props.setMenuSelectValue("dynamic");
      }
    }
    if (
      (this.props.sd.changeReason === ESdChangeReason.Undo || this.props.sd.changeReason === ESdChangeReason.Redo) &&
      prevProps.sd.changeReason !== this.props.sd.changeReason
    ) {
      this.props.sd.setComponentChildrenSettingsMainDi(newKuikaId, true);
    }
  }

  getSelectedMainComponentDi = (): IDisplayItem | undefined => {
    return this.getSelectedMainComponentDiBySd();
  };

  getSelectedMainComponentDiBySd = (): IDisplayItem | undefined => {
    return this.props &&
      this.props.sd &&
      this.props.sd.componentChildrenSettingsMainDi &&
      this.props.sd.componentChildrenSettingsMainDi
      ? this.props.sd.componentChildrenSettingsMainDi
      : undefined;
  };

  removeNode = (di: IDisplayItem): void => {
    if (di?.id && this.props?.sd?.deleteItemByKuikaId) {
      this.props.sd.deleteItemByKuikaId(di.id.toString());
    }
  };

  // Hides an "+ <child>" entry whose child cannot render on this project's
  // target — e.g. MixedChart's Bar, which is React-only. Without this the panel
  // would offer a child that silently generates nothing on a mobile project.
  isChildSupportedByProjectTarget = (screenDesignContext: IScreenDesignContextState, childName: string): boolean => {
    return isChildComponentSupportedByProjectTarget(
      {
        components: screenDesignContext.project?.componentSetObj?.components,
        projectType: screenDesignContext.project?.projectType,
        isWebScreen: screenDesignContext.getIsWebScreen?.() ?? true
      },
      childName
    );
  };

  getSelectedNodeStructure = (screenDesignContext: IScreenDesignContextState, item?: IDisplayItemComponent) => {
    let childrenNames: string[] = [];
    const sd = screenDesignContext.getScreenDesign();
    const selectedDisplayItem = screenDesignContext.getSelectedDisplayItem(sd);
    if (selectedDisplayItem && KMainFunctions.isObjectEmpty(selectedDisplayItem) !== true) {
      const component = item ?? selectedDisplayItem.item;
      const componentDefinition = screenDesignContext.getComponentDefinition(component);
      if (componentDefinition) {
        if (componentDefinition.childrenNames && componentDefinition.childrenNames.length > 0) {
          componentDefinition.childrenInfo?.forEach((c) => {
            if (c.fixed !== true && this.isChildSupportedByProjectTarget(screenDesignContext, c.name)) {
              childrenNames.push(c.name);
            }
          });
        }
      }
      if (
        selectedDisplayItem.item.name === "Table" ||
        selectedDisplayItem.item.name === "ReportTable" ||
        item?.name === "Table" ||
        item?.name === "ReportTable" ||
        item?.name === "StaticTable"
      ) {
        let tableHeader = selectedDisplayItem.children?.find(
          (child) =>
            child.item.name === "TableHeader" ||
            child.item.name === "ReportTableHeader" ||
            child.item.name === "StaticTableHeader"
        );
        const parentTable = ScreenDesignHelper.findParentTable(selectedDisplayItem, sd.displayItems);
        tableHeader =
          tableHeader ??
          parentTable?.children?.find(
            (child) =>
              child.item.name === "TableHeader" ||
              child.item.name === "ReportTableHeader" ||
              child.item.name === "StaticTableHeader"
          );
        let tableRow = selectedDisplayItem.children?.find(
          (child) =>
            child.item.name === "TableRow" ||
            child.item.name === "ReportTableRow" ||
            child.item.name === "StaticTableRow"
        );
        tableRow =
          tableRow ??
          parentTable?.children?.find(
            (child) =>
              child.item.name === "TableRow" ||
              child.item.name === "ReportTableRow" ||
              child.item.name === "StaticTableRow"
          );
        if (tableHeader) {
          childrenNames = childrenNames?.filter(
            (name) => name !== "TableHeader" && name !== "ReportTableHeader" && name !== "StaticTableHeader"
          );
        }
        if (tableRow) {
          childrenNames = childrenNames?.filter(
            (name) => name !== "TableRow" && name !== "ReportTableRow" && name !== "StaticTableRow"
          );
        }
      }
    }
    return childrenNames;
  };

  /**
   * The accepted children of ONE row. `getSelectedNodeStructure` resolves them against the current
   * selection; this drops a singleton the row already holds, so the row being drawn is what decides.
   */
  getAcceptsForRow = (item: IDisplayItemComponent, di: IDisplayItem): string[] => {
    const accepted: string[] = this.getSelectedNodeStructure(this.props.sd, item);
    if (accepted.length === 0) {
      return accepted;
    }
    const present: Set<string> = new Set<string>();
    (di.children ?? []).forEach((child: IDisplayItem) => {
      const childDefinition = this.props.sd.getComponentDefinition(child.item);
      const childName: string | undefined = childDefinition?.name ?? child.item?.name;
      if (childName) {
        present.add(childName);
      }
    });
    return accepted.filter((name: string) => !(SINGLETON_CHILDREN.includes(name) && present.has(name)));
  };

  reOrderItems = (sourceKuikaId: string, targetKuikaId: string, nextSelectedNodeDi: string) => {
    if (this.props && this.props.sd && this.props.sd.reOrderItems) {
      this.props.sd.reOrderItems(sourceKuikaId, targetKuikaId, nextSelectedNodeDi);
    }
  };

  getSiblings = (di: IDisplayItem): IDisplayItem[] | undefined => {
    const parentId = di?.parentId;
    if (!parentId) {
      return undefined;
    }
    const parent = this.props.sd.screenDesignDictionary[parentId];
    return parent?.children;
  };

  isFixed = (di: IDisplayItem) => {
    const parentId = di?.parentId;
    if (parentId) {
      const parent = this.props.sd.screenDesignDictionary[parentId];
      if (parent) {
        const parentCompDef = this.props.sd.getComponentDefinition(parent.item);
        if (parentCompDef) {
          const childInfo = parentCompDef.childrenInfo?.find((p) => p.name == di.item.name);
          if (childInfo?.fixed === true) {
            return true;
          }
        }
      }
    }
    return false;
  };

  canDelete = (di: IDisplayItem): boolean => {
    const mainDi = this.getSelectedMainComponentDi();
    if (mainDi?.id === di.id) {
      return false;
    }
    if (this.props.sd.isElementLocked(di.id)) {
      return false;
    }
    if (this.isFixed(di) === true) {
      return false;
    }
    return true;
  };

  canMoveUpNode = (di: IDisplayItem): boolean => {
    const siblings = this.getSiblings(di);
    if (!siblings) {
      return false;
    }
    if (this.props.sd.isElementLocked(di.id)) {
      return false;
    }
    if (this.isFixed(di) === true) {
      return false;
    }
    const idx = siblings.findIndex((x) => x.id === di.id);
    if (idx > 0) {
      if (this.isFixed(siblings[idx - 1]) === true) {
        return false;
      }
      return true;
    }
    return false;
  };

  canMoveDownNode = (di: IDisplayItem): boolean => {
    const siblings = this.getSiblings(di);
    if (!siblings) {
      return false;
    }
    if (this.props.sd.isElementLocked(di.id)) {
      return false;
    }
    if (this.isFixed(di) === true) {
      return false;
    }
    const idx = siblings.findIndex((x) => x.id === di.id);
    if (idx > -1 && idx < siblings.length - 1) {
      if (this.isFixed(siblings[idx + 1]) === true) {
        return false;
      }
      return true;
    }
    return false;
  };

  moveUpNode = (di: IDisplayItem): void => {
    const siblings = this.getSiblings(di);
    if (!siblings) {
      return;
    }
    const idx = siblings.findIndex((x) => x.id === di.id);
    if (idx > 0) {
      this.reOrderItems(di.id.toString(), siblings[idx - 1].id.toString(), di.id.toString());
    }
  };

  moveDownNode = (di: IDisplayItem): void => {
    const siblings = this.getSiblings(di);
    if (!siblings) {
      return;
    }
    const idx = siblings.findIndex((x) => x.id === di.id);
    if (idx > -1 && idx < siblings.length - 1) {
      this.reOrderItems(siblings[idx + 1].id.toString(), di.id.toString(), di.id.toString());
    }
  };

  addNode = (childName: string, item: IDisplayItem) => {
    const sd = this.props.sd.getScreenDesign();
    const selectedDisplayItem = this.props.sd.getSelectedDisplayItem(sd);
    if (!selectedDisplayItem) {
      return;
    }
    const kuikaId = item?.id ?? selectedDisplayItem.id;

    if (this.props && this.props.sd && this.props.sd.addChildItemByComponentName) {
      const isTableHeader =
        childName === "TableHeader" || childName === "ReportTableHeader" || childName === "StaticTableHeader";

      if (childName === "StaticTableRow") {
        const dict = this.props.sd.screenDesignDictionary;

        let columnKuikaId: string | undefined;

        if (item.item.name === "StaticTableColumn") {
          columnKuikaId = item.id.toString();
        } else if (item.item.name === "StaticTableRow") {
          const firstColumn = item.children?.find((child) => child.item.name === "StaticTableColumn");
          if (firstColumn) {
            columnKuikaId = firstColumn.id.toString();
          }
        } else if (item.item.name === "StaticTable") {
          const tableDi = dict[item.id];
          const lastRow = tableDi?.children?.filter((child) => child.item.name === "StaticTableRow").pop();
          const firstColumn = lastRow?.children?.find((child) => child.item.name === "StaticTableColumn");
          if (firstColumn) {
            columnKuikaId = firstColumn.id.toString();
          }
        }

        if (columnKuikaId) {
          const context = StaticTableHelper.getContext(sd, dict, columnKuikaId);
          if (context) {
            const newRowId = StaticTableHelper.insertRow(sd, dict, context, "below");
            this.props.sd.setScreenDesign(sd, ESdChangeReason.DragDropDi, newRowId.toString());
          }
        }
      } else {
        this.props.sd.addChildItemByComponentName(
          kuikaId.toString(),
          childName,
          isTableHeader ? PlaceToType.PlaceToFirstChildItem : PlaceToType.PlaceToLastChildItem
        );
      }
    }
  };

  /**
   * Tab, stepper and collapse items are not plain selections: picking one switches the canvas to
   * that pane. `rowIndex` is the node's place in the flattened tree, which is what the canvas takes
   * as the pane index (the root occupies index 0, hence the -1).
   */
  editNode = (node: IChildrenTreeNode, rowIndex: number, rootDi: IDisplayItem | undefined): void => {
    const kuikaId: number = node.kuikaId;
    const index: number = rowIndex === 0 ? -1 : rowIndex - 1;
    const rootKuikaId: string | undefined = rootDi?.id?.toString();

    if (node.componentName === "TabItem" && rootKuikaId) {
      this.props.sd.setSelectedTabItem(rootKuikaId, kuikaId.toString(), index);
    } else if (node.componentName === "StepperItem" && rootKuikaId) {
      this.props.sd.setSelectedStepItem(rootKuikaId, kuikaId.toString(), index);
    } else if (node.componentName === "CollapsePanel" && rootKuikaId) {
      this.props.sd.setSelectedCollapseItem(rootKuikaId, kuikaId.toString());
    } else {
      this.props.sd.setSelectedDisplayItem(kuikaId.toString());
    }
  };

  handleMenuSelectChange = (value: string, selectedDi: IDisplayItem | undefined) => {
    if (!selectedDi || !this.props.setMenuSelectValue) return;
    if (value === "static") {
      const optionsObj = {
        kuikaId: selectedDi.id.toString(),
        propertyName: "options",
        symbol: {
          category: 4,
          fixedValue: "[]",
          label: "Value",
          description: "Fixed Value",
          isFixed: false,
          MenuOptions: {}
        }
      } as IApplyPropertyGridChangesToDisplayParameters;
      const isDynamicObj = {
        kuikaId: selectedDi.id.toString(),
        propertyName: "isDynamic",
        symbol: {
          category: 4,
          fixedValue: "false",
          label: "Value",
          description: "Fixed Value"
        }
      } as IApplyPropertyGridChangesToDisplayParameters;
      this.props.sd.applyPropertyGridChangesToDisplay(isDynamicObj, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
      this.props.sd.applyPropertyGridChangesToDisplay(optionsObj, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
    } else if (value === "dynamic") {
      const isDynamicObj = {
        kuikaId: selectedDi.id.toString(),
        propertyName: "isDynamic",
        symbol: {
          category: 4,
          fixedValue: "true",
          label: "Value",
          description: "Fixed Value"
        }
      } as IApplyPropertyGridChangesToDisplayParameters;
      this.props.sd.applyPropertyGridChangesToDisplay(isDynamicObj, ESdChangeReason.ChangedCustomFieldOnPropertyPanel);
      this.props.sd.addItemToExpandedPropertyList("options");
    }
    this.props.setMenuSelectValue(value);
    this.forceUpdate();
  };

  toggleNode = (kuikaId: number): void => {
    this.setState((prevState) => {
      const collapsedNodes: number[] = [...prevState.collapsedNodes];
      const index: number = collapsedNodes.indexOf(kuikaId);
      if (index > -1) {
        collapsedNodes.splice(index, 1);
      } else {
        collapsedNodes.push(kuikaId);
      }
      return { collapsedNodes };
    });
  };

  /**
   * The parent segment of every add control's `data-testid`. It is the node's RAW derived title —
   * not the rendered label — because an E2E scenario names a node through the property grid and
   * then addresses the add control by that name.
   */
  private getAddTestIdSegment = (node: IChildrenTreeNode): string => {
    if (node.isRoot) {
      return "root";
    }
    return String(node.title ?? node.componentName).replace(/\s/g, "");
  };

  private renderAddControl = (node: IChildrenTreeNode): React.ReactNode => {
    if (node.accepts.length === 0) {
      return null;
    }
    const segment: string = this.getAddTestIdSegment(node);

    // One accepted child means there is nothing to choose: the button adds it, exactly as the old
    // single "+ <child>" slot did. A menu would put a click between the user and the only answer.
    if (node.accepts.length === 1) {
      const child: string = node.accepts[0];
      return (
        <KuikaTooltip content={`Add ${child}`} instantExit>
          <KuikaButton
            className="kuika-children-tree__add kuika_selectedNodeAddButton"
            id={`component-children-settings-add-button-${child}`}
            data-testid={`children-add-button-${child}-${segment}`}
            type="tertiary"
            color="neutral"
            isIcon
            aria-label={`Add ${child}`}
            onClick={(e?: React.MouseEvent<HTMLButtonElement>) => {
              e?.stopPropagation();
              this.addNode(child, node.di);
            }}
          >
            <KuikaSvg name="plus" width={16} height={16} />
          </KuikaButton>
        </KuikaTooltip>
      );
    }

    // Two or more: a menu, so the list grows with the component definition instead of being capped
    // at the two halves the old add bar could hold (MixedChart offers three).
    const options: KuikaDropdownMenuItemType[] = node.accepts.map((child: string) => ({
      key: child,
      label: child,
      icon: <KuikaSvg name="plus" width={16} height={16} />,
      dataTestId: `children-add-button-${child}-${segment}`,
      onClick: () => this.addNode(child, node.di)
    }));

    return (
      <span
        className="kuika-children-tree__add"
        onClick={(e: React.MouseEvent<HTMLSpanElement>) => e.stopPropagation()}
      >
        <KuikaDropdownMenu
          options={options}
          header={`Add to ${node.title || node.componentName}`}
          placement="bottomRight"
        >
          {(open: boolean) => (
            <KuikaButton
              className="kuika_selectedNodeAddButton"
              id={`component-children-settings-add-button-${node.accepts[0]}`}
              data-testid={`children-add-trigger-${segment}`}
              type="tertiary"
              color="neutral"
              isIcon
              active={open}
              aria-label={`Add a child to ${node.title || node.componentName}`}
            >
              <KuikaSvg name="plus" width={16} height={16} />
            </KuikaButton>
          )}
        </KuikaDropdownMenu>
      </span>
    );
  };

  private renderRowActions = (node: IChildrenTreeNode): React.ReactNode => {
    if (this.props.disableChildActions || node.isRoot) {
      return null;
    }
    return (
      <div className="kuika-children-tree__reveal">
        <KuikaTooltip content="Move up" instantExit>
          <KuikaButton
            data-testid="childrenMoveUp"
            type="tertiary"
            color="neutral"
            isIcon
            disabled={!node.canMoveUp}
            aria-label="Move up"
            onClick={(e?: React.MouseEvent<HTMLButtonElement>) => {
              e?.stopPropagation();
              this.moveUpNode(node.di);
            }}
          >
            <KuikaSvg name="arrow-up" type="arrows" width={16} height={16} />
          </KuikaButton>
        </KuikaTooltip>
        <KuikaTooltip content="Move down" instantExit>
          <KuikaButton
            data-testid="childrenMoveDown"
            type="tertiary"
            color="neutral"
            isIcon
            disabled={!node.canMoveDown}
            aria-label="Move down"
            onClick={(e?: React.MouseEvent<HTMLButtonElement>) => {
              e?.stopPropagation();
              this.moveDownNode(node.di);
            }}
          >
            <KuikaSvg name="arrow-down" type="arrows" width={16} height={16} />
          </KuikaButton>
        </KuikaTooltip>
        <KuikaTooltip content="Delete" instantExit>
          <KuikaButton
            data-testid="childrenDelete"
            type="tertiary"
            color="danger"
            isIcon
            disabled={!node.canDelete}
            aria-label="Delete"
            onClick={(e?: React.MouseEvent<HTMLButtonElement>) => {
              e?.stopPropagation();
              this.removeNode(node.di);
            }}
          >
            <KuikaSvg name="trash" width={16} height={16} />
          </KuikaButton>
        </KuikaTooltip>
      </div>
    );
  };

  private renderRow = (
    node: IChildrenTreeNode,
    rowIndex: number,
    rootDi: IDisplayItem | undefined
  ): React.ReactNode => {
    const label: string = node.title || node.componentName;
    const rails: React.ReactNode[] = [];
    for (let level = 0; level < node.depth; level++) {
      rails.push(<span key={level} className="kuika-children-tree__rail" style={{ width: RAIL_WIDTH_PX }} />);
    }

    return (
      <div
        key={node.kuikaId}
        // The `kuika_*` names carry no styling any more. They stay because an onboarding tooltip is
        // stored as a CSS selector in the database and can still be pointed at one of them.
        className={classNames("kuika-children-tree__row", OVERFLOW_REVEAL_HOST_CLASS, {
          "kuika-children-tree__row--selected": node.isSelected,
          kuika_SelectedNodeDi: node.isSelected,
          kuika_AcceptedChildrenNodeDi: node.accepts.length > 0,
          kuika_NonSelectedNodeDi: !node.isSelected,
          [`kuika_NonSelectedNodeDi-${node.componentName.replace(/\s/g, "").toLowerCase()}`]: true
        })}
        data-selected={node.isSelected ? "true" : "false"}
        data-testid={`children-tree-row-${node.kuikaId}`}
        onClick={() => this.editNode(node, rowIndex, rootDi)}
      >
        {rails}
        {node.hasChildren ? (
          <span
            className={classNames("kuika-children-tree__caret", {
              "kuika-children-tree__caret--closed": !node.expanded
            })}
            role="button"
            aria-label={node.expanded ? "Collapse" : "Expand"}
            data-testid={`children-tree-caret-${node.kuikaId}`}
            onClick={(e: React.MouseEvent<HTMLSpanElement>) => {
              e.stopPropagation();
              this.toggleNode(node.kuikaId);
            }}
          >
            <KuikaSvg name="chevron-down" type="arrows" width={12} height={12} />
          </span>
        ) : (
          <span className="kuika-children-tree__caret kuika-children-tree__caret--leaf" />
        )}
        <KuikaComponentIcon src={node.iconUrl} width={16} height={16} className="kuika-children-tree__icon" />
        <KuikaText
          className={`kuika-children-tree__label component-children-settings-title-${label
            .replace(/\s/g, "")
            .toLowerCase()}`}
          // KuikaText only derives its native title from plain-string children, and the reveal
          // wrapper is an element — state it so a truncated name is still readable on hover.
          title={label}
        >
          <KuikaOverflowText>{label}</KuikaOverflowText>
        </KuikaText>
        <div className="kuika-children-tree__actions">
          {this.renderRowActions(node)}
          {!this.props.disableChildActions && this.renderAddControl(node)}
        </div>
      </div>
    );
  };

  render() {
    const mainDi: IDisplayItem | undefined = this.getSelectedMainComponentDi();
    if (mainDi === undefined) return <></>;

    const selectedDi = this.props.sd.getSelectedDisplayItem(this.props.sd.getScreenDesign());
    if (selectedDi && selectedDi.id === -1) return <></>;
    if (
      this.props.sd.dashboardState !== DashboardState.design &&
      this.props.sd.dashboardState !== DashboardState.reportDesigner
    )
      return <></>;

    const deps: IChildrenTreeDeps = {
      getComponentDefinition: (item?: IDisplayItemComponent) => this.props.sd.getComponentDefinition(item),
      getAccepts: this.getAcceptsForRow,
      isCollapsed: (kuikaId: number) => this.state.collapsedNodes.indexOf(kuikaId) > -1,
      isSelected: (kuikaId: number) => selectedDi?.id === kuikaId,
      canDelete: this.canDelete,
      canMoveUp: this.canMoveUpNode,
      canMoveDown: this.canMoveDownNode
    };
    const rows: IChildrenTreeNode[] = buildChildrenTree(mainDi, deps);

    // A component with neither children nor anything it could accept has no structure to show. A
    // childless one that DOES accept a child still renders, so a freshly dropped Table offers its
    // header and row instead of an empty properties column.
    if (rows.length === 0) return <></>;
    if (rows.length === 1 && rows[0].accepts.length === 0) return <></>;

    const isMobileApp = this.props.sd.project?.projectType === EProjectType.Mobile;
    return (
      <>
        {selectedDi && selectedDi.item.name === "Menu" && !isMobileApp && (
          <div className="kuika-right-panel__card--wrapper" style={{ marginBottom: 0 }}>
            <KuikaSmallMediumText style={{ paddingBottom: 4 }}>Select a Data Type</KuikaSmallMediumText>
            <KuikaSelectbox
              className="kuika_add_actions_inputs_size"
              defaultValue={
                selectedDi.item.properties.find((x) => x.name == "isDynamic")?.symbol?.fixedValue === "true"
                  ? "dynamic"
                  : "static"
              }
              value={this.props.menuSelectValue}
              onChange={(e) => this.handleMenuSelectChange(e as string, selectedDi)}
              style={{ width: "100%", margin: 0, padding: 0 }}
              options={[
                { value: "static", label: "Static" },
                { value: "dynamic", label: "Dynamic" }
              ]}
            />
          </div>
        )}
        {(selectedDi && selectedDi.item.name === "Menu" && (this.props.menuSelectValue === "dynamic" || isMobileApp)) ||
        (isMobileApp && (selectedDi?.item?.name === "MenuItem" || selectedDi?.item.name === "SubMenu")) ? (
          <></>
        ) : (
          <div className="kuika-children-tree kuika_sortableTreePanel" data-testid="componentChildrenTree">
            <div className="kuika-children-tree__rows">
              <KuikaSelectionIndicator />
              {rows.map((node: IChildrenTreeNode, index: number) => this.renderRow(node, index, mainDi))}
            </div>
          </div>
        )}
      </>
    );
  }

  checkNeedRender = (changeReason: ESdChangeReason): boolean => {
    if (
      changeReason === ESdChangeReason.PropertyFixedValueChanged ||
      changeReason === ESdChangeReason.ChangeSelectedDiByCanvasFrame ||
      changeReason === ESdChangeReason.ChangeSelectedDiByTools ||
      changeReason === ESdChangeReason.DragDropDi ||
      changeReason === ESdChangeReason.DragDropBlock ||
      changeReason === ESdChangeReason.ChangeResponsiveRowColumnSettings ||
      changeReason === ESdChangeReason.ChangedCustomFieldOnPropertyPanel ||
      changeReason === ESdChangeReason.DesignTimeElementStateChanged
    ) {
      return true;
    }
    return false;
  };
}

const componentChildrenSettings = withSD<IComponentChildrenSettingsProps>(ComponentChildrenSettings);
export { componentChildrenSettings as ComponentChildrenSettings };
